import manufacturerModelModule from '@/store/modules/admin/manufacturer-model';
import { createEmptyRow, fillRealValues, fillSummaryValues } from './internal-utils';


export function buildTableStructure(
  summaryJson: any,
  monthHeaders: string[],
  rangeFrom: any,
  gCommonList: (arg: string) => any[],
  viewType: string
) {
  const tableRows: any[] = [];
  const editableData: any = {};

  const totalMainColumns = monthHeaders.length + 2;

  const globalTotals = new Array(totalMainColumns).fill(0);

  const centersMap: { [key: string]: string } = {};
  const totalVins: any[] = [];

  for (const centro in summaryJson.actual) {
    if (shouldIncludeCenter(centro, viewType)) {
      const normalized = centro.trim().toLowerCase();
      if (!centersMap[normalized]) {
        centersMap[normalized] = toCanonicalName(centro);
      }
    }
  }
  for (const centro in summaryJson.anio_anterior) {
    if (shouldIncludeCenter(centro, viewType)) {
      const normalized = centro.trim().toLowerCase();
      if (!centersMap[normalized]) {
        centersMap[normalized] = toCanonicalName(centro);
      }
    }
  }
  delete centersMap['conteo'];

  Object.entries(centersMap).forEach(([normalizedCentro, displayCentro]) => {
    const centerRowKey = `center-${normalizedCentro}`;
    tableRows.push({ label: displayCentro, key: centerRowKey });

    editableData[centerRowKey] = createEmptyRow(totalMainColumns);

    const centerTotals = new Array(totalMainColumns).fill(0);
    const centerObjectives = new Array(totalMainColumns).fill(0);

    const toArray = (data: any): any[] => {
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && Array.isArray(data.Detalle)) return data.Detalle;
      return [];
    };
    const actualItems = toArray(findDataByNormalizedKey(summaryJson.actual, normalizedCentro) || []);
    const previousItems = toArray(findDataByNormalizedKey(summaryJson.anio_anterior, normalizedCentro) || []);

    const modelMap: { [modelo: string]: any[] } = {};
    actualItems.forEach(item => {
      item.dealer = displayCentro;
      const modeloKey = item.ModeloDn.trim().toLowerCase();
      if (!modelMap[modeloKey]) modelMap[modeloKey] = [];
      modelMap[modeloKey].push(item);

      if (item.vin) {
        totalVins.push({ ...item, month: item.Mes, dealer: displayCentro });
      }
    });
    previousItems.forEach(item => {
      const modeloKey = item.ModeloDn.trim().toLowerCase();
      if (!modelMap[modeloKey]) modelMap[modeloKey] = [];
      modelMap[modeloKey].push(item);
    });

    Object.keys(modelMap).forEach(modeloKey => {
      const canonicalModelo = toCanonicalName(modelMap[modeloKey][0].ModeloDn);
      const itemRowKey = `item-${normalizedCentro}-${modeloKey}`;

      tableRows.push({ label: canonicalModelo, key: itemRowKey });

      editableData[itemRowKey] = createEmptyRow(totalMainColumns);

      fillRealValues(
        modelMap[modeloKey],
        itemRowKey,
        editableData,
        monthHeaders,
        rangeFrom,
        centerTotals,
        globalTotals,
        gCommonList
      );

      for (let i = 0; i < totalMainColumns; i++) {
        const objVal = Number(editableData[itemRowKey][i]?.objetivo || 0);
        centerObjectives[i] += objVal;
      }
    });

    for (let idx = 0; idx < totalMainColumns; idx++) {
      editableData[centerRowKey][idx].real = centerTotals[idx];
      editableData[centerRowKey][idx].objetivo = centerObjectives[idx];
    }
  });

  tableRows.push({
    label: 'Total',
    key: 'globalTotal',
    vins: totalVins
  });
  editableData['globalTotal'] = createEmptyRow(totalMainColumns);
  for (let idx = 0; idx < totalMainColumns; idx++) {
    editableData['globalTotal'][idx].real = globalTotals[idx];
  }

  return { tableRows, editableData, centersMap };
}

function toCanonicalName(name: string): string {
  return name
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function findDataByNormalizedKey(dataObj: any, normalizedKey: string): any {
  for (const key in dataObj) {
    if (key.trim().toLowerCase() === normalizedKey) {
      return dataObj[key];
    }
  }
  return null;
}

export function buildSummaryRows(
  summaryJson: any,
  monthHeaders: string[],
  rangeFrom: any,
  gCommonList: (arg: string) => any[],
  viewType: string
) {
  const allActualItems: any[] = [];
  Object.entries(summaryJson.actual).forEach(([centro, rawData]) => {
    if (!shouldIncludeCenter(centro, viewType)) return;

    let arr: any[] = [];
    if (Array.isArray(rawData)) {
      arr = rawData;
    } else if (rawData && typeof rawData === 'object' && 'Detalle' in rawData) {
      arr = (rawData as { Detalle: any[] }).Detalle;
    }
    allActualItems.push(...arr);
  });

  const allPrevItems: any[] = [];
  Object.entries(summaryJson.anio_anterior).forEach(([centro, rawData]) => {
    if (!shouldIncludeCenter(centro, viewType)) return;

    let arr: any[] = [];
    if (Array.isArray(rawData)) {
      arr = rawData;
    } else if (rawData && typeof rawData === 'object' && 'Detalle' in rawData) {
      arr = (rawData as { Detalle: any[] }).Detalle;
    }
    allPrevItems.push(...arr);
  });

  const modelMap: { [modelo: string]: any[] } = {};
  allActualItems.forEach(item => {
    const modelo = item.ModeloDn;
    if (!modelMap[modelo]) {
      modelMap[modelo] = [];
    }
    modelMap[modelo].push(item);
  });
  const prevModelMap: { [modelo: string]: any[] } = {};
  allPrevItems.forEach(item => {
    const modelo = item.ModeloDn;
    (prevModelMap[modelo] = prevModelMap[modelo] ?? []).push(item);
  });

  const totalColumns = monthHeaders.length + 2;
  const rows: any[] = [];
  const totalVins: any[] = [];

  for (const modelo in modelMap) {
    if (!modelMap[modelo].some((item: any) => item.Anyo === rangeFrom.year)) {
      continue;
    }
    const cells = createEmptyRow(totalColumns);
    const vins: any[] = [];

    fillSummaryValues(
      modelMap[modelo],
      cells,
      monthHeaders,
      rangeFrom,
      gCommonList
    );

    modelMap[modelo].forEach((item: any) => {
      if (item.vin) {
        const vinItem = { ...item, month: item.Mes };
        vins.push(vinItem);
        totalVins.push(vinItem);
      }
    });

    const prevItemsForModel = prevModelMap[modelo] || [];
    const prevTotal = prevItemsForModel.reduce((sum, it) => {
      return sum + Number(it.Cantidad ?? it.Real ?? 0);
    }, 0);
    cells[monthHeaders.length + 1].real = prevTotal;

    const allZeros = cells.every(cell => cell.real === 0 && cell.objetivo === 0);
    if (allZeros) {
      continue;
    }

    rows.push({
      label: modelo,
      values: cells,
      key: modelo,
      vins: vins
    });
  }

  const totalCells = createEmptyRow(totalColumns);
  rows.forEach(row => {
    row.values.forEach((cell: { real: number; objetivo: number }, i: number) => {
      totalCells[i].real += cell.real;
      totalCells[i].objetivo += cell.objetivo;
    });
  });
  rows.push({
    label: 'Total',
    values: totalCells,
    key: 'globalTotal',
    vins: totalVins,
  });

  return rows;
}

export function shouldIncludeCenter(centerName: string, viewType: string): boolean {
  const upperName = centerName.toUpperCase();
  const isInternal = upperName.includes('REVO') || upperName.includes('RM');

  switch (viewType) {
    case 'internal':
      return isInternal;
    case 'external':
      return !isInternal;
    case 'total':
    default:
      return true;
  }
}


export function buildObjectivesArray(
  changesTracker: Set<string>,
  editableData: any,
  centersMap: { [key: string]: string },
  rangeFrom: { year: number; from: number },
  manufacturerId: string
) {
  const objectives: any[] = [];

  changesTracker.forEach(changeKey => {
    const [dealerKey, modelKey, yearStr, monthStr] = changeKey.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    const dealerActual = centersMap[dealerKey.toLowerCase()];
    if (!dealerActual) return;

    const columnIndex = month - rangeFrom.from - 1;
    if (!editableData[`item-${dealerKey}-${modelKey}`]?.[columnIndex]) {
      console.warn('No hay datos para row/col:', `item-${dealerKey}-${modelKey}`, columnIndex);
      return;
    }

    const monthly_target = +editableData[`item-${dealerKey}-${modelKey}`][columnIndex].objetivo;

    if (monthly_target < 0) return;

    objectives.push({
      manufacturer_id: manufacturerId,
      dealer: dealerActual,
      model: modelKey,
      year,
      month,
      monthly_target
    });
  });

  return objectives;
}
